import { Component, inject, OnInit } from '@angular/core';
import { NgbActiveModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { AssetFuelChangesResponse, AssetFuelLevelResponse, AssetFuelLevelSummaryResponse, AutoValidationRequest, FuelChangesDetails, FuelDecreaseInvestigationRequest } from '../../../models/fuel-changes-details.model';
import { CommonModule } from '@angular/common';
import { FuelChangesService } from '../../../services/fuel-changes.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { Subject } from 'rxjs';
import { CommonMap } from '../../../../_shared';
import { IClusterRenderer } from '../../../../../_shared/interfaces/maps/base-cluster-renderer';
import { IMarkerFactory } from '../../../../../_shared/interfaces/maps/base-marker-factory';
import { FuelChangesMarkerFactory } from '../../../helpers/maps/fuel-changes-marker-factory.helper';
import { FuelChangesClusterRenderer } from '../../../helpers/maps/fuel-changes-cluster-renderer.helper';
import { ToastrService } from 'ngx-toastr';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-validation-modal',
  standalone: true,
  imports: [CommonModule, CommonMap, NgbTooltip, EmptyStateComponent, TranslocoDirective],
  templateUrl: './validation-modal.component.html',
  styleUrl: './validation-modal.component.scss'
})
export class ValidationModal implements OnInit {
  isLoading: boolean = false;
  isCurrentFuelLevelLoading: boolean = false;
  isSubmitting: boolean = false;
  isFuelSummaryLoading: boolean = false;
  isFuelChangesLoading: boolean = false;
  fuelChangeDetail: FuelChangesDetails;
  assetFuelLevel: AssetFuelLevelResponse | null;
  assetFuelChanges: AssetFuelChangesResponse[] | [];
  assetFuelLevelSummary: AssetFuelLevelSummaryResponse | null;
  investigationRequest: FuelDecreaseInvestigationRequest;
  validationRequest: AutoValidationRequest;
  totalNetFuelChange: number = 0;
  selectedAction: string = '';
  selectedReason: string = '';
  discrepancyReason: string = '';
  private readonly modalRef = inject(NgbActiveModal);
  private transloco = inject(TranslocoService);
  private cancelSubject: Subject<void> = new Subject<void>();

  mapCenter: { lat: number, lng: number } = { lat: 0, lng: 0 };
  clusterRenderer: IClusterRenderer = new FuelChangesClusterRenderer();
  markerFactory: IMarkerFactory = new FuelChangesMarkerFactory();
  mapData: any[] = [];
  mapTypeControl: boolean = true;

  constructor(
    private fuelChangesService: FuelChangesService,
    private errorHandlerService: ErrorHandlerService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    if (this.fuelChangeDetail && this.fuelChangeDetail.lat && this.fuelChangeDetail.lng) {
      this.mapCenter = {
        lat: this.fuelChangeDetail.lat,
        lng: this.fuelChangeDetail.lng
      };
    }

    this.getFuelDecreaseInvestigation();
    this.getCurrentFuelLevel();
    this.getAssetFuelChanges();
    this.getAssetFuelLevelSummary();
    this.initializeFilteredData();
  }

  getValidationLabel(value: string): string {
    const keys: Record<string, string> = {
    "Valid": "fuelChanges.validationModal.options.valid",
    "Invalid": "fuelChanges.validationModal.options.invalid",
    "Lawful Removal": "fuelChanges.validationModal.options.lawfulRemoval",
    "Tank Sharing": "fuelChanges.validationModal.options.tankSharing",
    "Contamination": "fuelChanges.validationModal.options.contamination",
    "Pilfering": "fuelChanges.validationModal.options.pilfering",
    "Other": "fuelChanges.validationModal.options.other"
};
    if (value === 'Reason') return this.transloco.translate('fuelChanges.validationModal.reason');
    return keys[value] ? this.transloco.translate(keys[value]) : value;
  }

  selectAction(action: string) {
    this.selectedAction = action;
  }

  selectReason(reason: string) {
    this.selectedReason = reason;
  }

  onEnterPressed(event: any) {
    event.preventDefault();
    this.selectedReason = event.target.value;
  }

  get isSubmitEnabled(): boolean {
    return this.selectedAction !== '' && this.selectedReason !== '';
  }

  get hasAssetFuelChanges(): boolean {
    return this.assetFuelChanges && this.assetFuelChanges.length > 0;
  }

  initializeFilteredData() {
    this.mapData = [];
    if (this.fuelChangeDetail) {
      this.mapData.push({
        ...this.fuelChangeDetail,
        type: 'Loss',
        isActive: true
      });
    }
  }

  onLocationClick(latitude?: number, longitude?: number, fuelValue?: number) {
    if (latitude && longitude) {
      this.mapCenter = { lat: latitude, lng: longitude };

      this.mapData = [{
        lat: latitude,
        lng: longitude,
        type: fuelValue && fuelValue > 0 ? 'Fill-Up' : 'Loss',
        isActive: true
      }];
    }
  }

  async getFuelDecreaseInvestigation() {
    try {
      var assetId = this.fuelChangeDetail.assetId;
      var response = await this.fuelChangesService.getFuelDecreaseInvestigation(assetId, this.fuelChangeDetail.fuelCostDetailId, this.cancelSubject);
      if (response.data) {
        this.selectedAction = response.data.validationStatus;
        this.selectedReason = response.data.reason;
      }
    } catch (error: any) {
      this.errorHandlerService.handleError(error);
    }
  }

  async submitDecreaseInvestigation() {
    try {
      this.isSubmitting = true;
      this.investigationRequest = {
        validationStatus: this.selectedAction,
        reason: this.selectedReason,
        assetId: this.fuelChangeDetail.assetId,
        fuelCostDetailId: this.fuelChangeDetail.fuelCostDetailId
      };

      await this.fuelChangesService.upsertFuelDecreaseInvestigation(this.investigationRequest, this.cancelSubject);
      this.toastr.success(this.transloco.translate('fuelChanges.validationModal.investigationSubmitted'), '', {
        timeOut: 1000
      });
    } catch (error: any) {
      this.errorHandlerService.handleError(error);
    } finally {
      this.closeModal();
    }
  }

  async autoValidateDecrease() {
    try {
      this.isLoading = true;
      this.validationRequest = {
        assetId: this.fuelChangeDetail.assetId,
        decreaseTimeSent: this.fuelChangeDetail.timestamp,
        decreaseFuelLevel: - this.fuelChangeDetail.amount
      };

      var response = await this.fuelChangesService.AutoValidateDecrease(this.validationRequest, this.cancelSubject);
      if (response.data) {
        this.selectedAction = response.data?.validationStatus;
        this.selectedReason = response.data?.reason ?? 'Reason';
        this.discrepancyReason = response.data?.discrepancyReason ?? '';
      }

    } catch (error: any) {
      this.errorHandlerService.handleError(error);
    } finally {
      this.isLoading = false;
    }
  }

  async getCurrentFuelLevel() {
    try {
      this.isCurrentFuelLevelLoading = true;
      var response = await this.fuelChangesService.getCurrentFuelLevel(this.fuelChangeDetail.assetId, this.cancelSubject);
      this.assetFuelLevel = response.data ?? null;
    } catch (error: any) {
      this.errorHandlerService.handleError(error);
    } finally {
      this.isCurrentFuelLevelLoading = false;
    }
  }

  async getAssetFuelChanges() {
    try {
      this.isFuelChangesLoading = true;
      var response = await this.fuelChangesService.getFuelChanges(
        this.fuelChangeDetail.assetId,
        this.fuelChangeDetail.timestamp,
        this.cancelSubject);
      this.assetFuelChanges = response.data ?? [];
      this.totalNetFuelChange = this.assetFuelChanges.reduce((sum, x) =>
        sum + (x.fuelChange ?? 0), 0
      );
    } catch (error: any) {
      this.errorHandlerService.handleError(error);
    } finally {
      this.isFuelChangesLoading = false;
    }
  }

  async getAssetFuelLevelSummary() {
    try {
      this.isFuelSummaryLoading = true;
      var response = await this.fuelChangesService.getAssetFuelChangesSummary(
        this.fuelChangeDetail.assetId,
        this.fuelChangeDetail.timestamp,
        this.cancelSubject);
      this.assetFuelLevelSummary = response.data ?? null;
    } catch (error: any) {
      this.errorHandlerService.handleError(error);
    } finally {
      this.isFuelSummaryLoading = false;
    }
  }

  closeModal(): void {
    this.modalRef.close();
  }
}